import { stringify } from 'yaml';
import { loadAutomationModels } from '../migration/LegacyEventsToAst.js';
import { AutomationModel } from '../ast/AutomationModel.js';
import { AutomationCompiler } from '../compiler/AutomationCompiler.js';
import { materializeTtsAction } from '../mediaPlayerTtsAction.js';
import { toHomeAssistantAction } from '../haActionYaml.js';
import { formatOpenHASPPluginObjectRef } from './pluginPropertyBindings.js';
import { renderConditionTemplate } from './conditionTemplate.js';
import { toRaw } from 'vue';
import {
  btnmatrixFilterExpression,
  btnmatrixFilterValue,
  findBtnmatrixButton,
  getBtnmatrixButtons,
  isValidBtnmatrixFilter,
} from '../btnmatrixButtons.js';

const compiler = new AutomationCompiler();
const HOME_ASSISTANT_YAML_OPTIONS = { lineWidth: 0, indent: 2 };

export function stringifyHomeAssistantAutomations(automations) {
  return stringify(automations, HOME_ASSISTANT_YAML_OPTIONS).trimEnd() + '\n';
}

function clone(value) {
  if (Array.isArray(value)) return value.map(clone);
  if (value && typeof value === 'object') {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, clone(item)]));
  }
  return value;
}

/** Translate Designer-only event variables into Home Assistant trigger data. */
export function translateHomeAssistantRuntimeExpressions(value) {
  if (Array.isArray(value)) return value.map(translateHomeAssistantRuntimeExpressions);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, translateHomeAssistantRuntimeExpressions(item)])
    );
  }
  if (typeof value !== 'string') return value;
  return value
    .replace(/\{\{\s*val\b/g, '{{ trigger.payload_json.val')
    .replace(/\{\{\s*start\b/g, '{{ trigger.payload_json.start')
    .replace(/\{\{\s*tag\b/g, '{{ trigger.payload_json.tag');
}

function entityValue(action) {
  const parameter = action.parameters?.find(item => item.key === 'entity_id');
  if (!parameter?.value) return null;
  if (parameter.value.kind !== 'literal' && parameter.value.kind !== 'list') return null;
  return parameter.value.kind === 'list'
    ? [...parameter.value.items]
    : parameter.value.toString();
}

function replaceImplicitTagTarget(value, action) {
  const target = value.target;
  const entityId = target?.entity_id;
  const hasImplicitTag = typeof entityId === 'string'
    ? /\{\{\s*tag\b/.test(entityId)
    : Array.isArray(entityId) && entityId.some(item => /\{\{\s*tag\b/.test(String(item)));
  if (!hasImplicitTag) return value;

  const selectedEntity = entityValue(action);
  if (selectedEntity) value.target = { ...target, entity_id: selectedEntity };
  else delete value.target;
  return value;
}

function homeAssistantAction(action, object) {
  if (action.type === 'delay') {
    const seconds = action.parameters?.find(item => item.key === 'seconds')?.value?.toString() ?? '1';
    return { delay: { seconds } };
  }

  const materialized = materializeTtsAction(action);
  const value = replaceImplicitTagTarget(
    clone(toHomeAssistantAction(materialized, { useTag: false, object })),
    materialized,
  );
  return translateHomeAssistantRuntimeExpressions(value);
}

function safeId(value) {
  return String(value ?? 'openhasp').replace(/[^a-zA-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'openhasp';
}

function triggerFor({ plateName, page_id, hasp_id, object, event }) {
  const objectRef = formatOpenHASPPluginObjectRef(page_id, object, hasp_id);
  return {
    trigger: 'mqtt',
    topic: `hasp/${plateName}/state/${objectRef}`,
    value_template: '{{ value_json.event }}',
    payload: event,
  };
}

/**
 * Generate actual Home Assistant automations for openHASP widget events.
 *
 * This is deliberately separate from HomeAssistantGenerator: that generator
 * emits the openHASP integration's `objects:` component configuration, while
 * this output is consumed by Home Assistant's automation/script YAML parser.
 */
/**
 * Build pure Home Assistant automation objects from the Designer models.
 *
 * No YAML syntax belongs in this layer. Keeping this as a separate boundary
 * makes it impossible for trigger/action nesting to depend on indentation.
 */
export function buildHomeAssistantAutomationObjects({
  page_id,
  hasp_id,
  object,
  hass_config,
  plateName = 'openhasp',
  validationErrors,
}) {
  const models = loadAutomationModels({ page_id, hasp_id, object, hass_config })
    .filter(model => model.triggers.length > 0 && model.actions.length > 0);
  const { models: compiled, diagnostics } = compiler.compile(models);
  if (validationErrors && diagnostics.length > 0) {
    validationErrors.push(...diagnostics.map(diagnostic => diagnostic.message));
  }

  const automations = [];
  compiled.forEach((model, modelIndex) => {
    const widgetTriggers = model.triggers.filter(trigger =>
      trigger?.source === 'widget' && trigger.event,
    );
    if (widgetTriggers.length === 0) return;
    const event = widgetTriggers[0].event;

    const filterValue = object?.obj === 'btnmatrix'
      ? btnmatrixFilterValue(model.triggers[0])
      : null;
    if (filterValue !== null &&
        (!isValidBtnmatrixFilter(model.triggers[0]) ||
         !findBtnmatrixButton(getBtnmatrixButtons(object), filterValue))) {
      validationErrors?.push(
        `Object p${page_id}b${hasp_id}: btnmatrix event ${event} references missing button index ${filterValue}.`,
      );
    }

    const objectRef = formatOpenHASPPluginObjectRef(page_id, object, hasp_id);
    const automation = {
      id: `openhasp_${safeId(plateName)}_${safeId(objectRef)}_${safeId(event)}_${modelIndex}`,
      alias: `openHASP ${plateName} ${objectRef} ${event}`,
      triggers: widgetTriggers.map(trigger => triggerFor({
        plateName, page_id, hasp_id, object, event: trigger.event,
      })),
      actions: model.actions.map(action => homeAssistantAction(action, object)),
    };
    const triggerFilter = filterValue;
    const conditions = [
      ...(triggerFilter !== null
        ? [{
          condition: 'template',
          value_template: translateHomeAssistantRuntimeExpressions(
            btnmatrixFilterExpression(model.triggers[0]) ?? '{{ false }}',
          ),
        }]
        : []),
      ...model.conditions.map(condition => ({
        condition: 'template',
        value_template: translateHomeAssistantRuntimeExpressions(renderConditionTemplate(condition)),
      })),
    ];
    if (conditions.length > 0) {
      automation.conditions = conditions;
    }
    automations.push(automation);
  });

  return automations;
}

function stateTriggerFor(trigger) {
  return {
    trigger: 'state',
    entity_id: trigger.entityId,
    ...(trigger.attribute ? { attribute: trigger.attribute } : {}),
    ...(trigger.from ? { from: trigger.from } : {}),
    ...(trigger.to ? { to: trigger.to } : {}),
  };
}

/**
 * Build Home Assistant automations for the state triggers of a page's flow
 * graph (hassCompiler `pageRules`): an entity's state change runs actions,
 * optionally behind conditions. They belong to the page, not to an object.
 */
export function buildHomeAssistantStateAutomations({ page_id, rules = [], plateName = 'openhasp', validationErrors }) {
  const models = rules.map(rule => AutomationModel.fromJSON(rule))
    .filter(model => model.triggers.length > 0 && model.actions.length > 0);
  const { models: compiled, diagnostics } = compiler.compile(models);
  if (validationErrors && diagnostics.length > 0) {
    validationErrors.push(...diagnostics.map(diagnostic => diagnostic.message));
  }
  const automations = [];
  compiled.forEach((model, modelIndex) => {
    const triggers = model.triggers.filter(trigger => trigger?.source === 'state' && trigger.entityId);
    if (triggers.length === 0) return;
    const entityId = triggers[0].entityId;
    const automation = {
      id: `openhasp_${safeId(plateName)}_p${page_id}_state_${safeId(entityId)}_${modelIndex}`,
      alias: `openHASP ${plateName} p${page_id} ${entityId} state`,
      triggers: triggers.map(stateTriggerFor),
      actions: model.actions.map(action => homeAssistantAction(action, null)),
    };
    if (model.conditions.length > 0) {
      automation.conditions = model.conditions.map(condition => ({
        condition: 'template',
        value_template: renderConditionTemplate(condition),
      }));
    }
    automations.push(automation);
  });
  return automations;
}

/**
 * Collect automation objects from every regular-page object and each template
 * instance attached to a runtime page. Template widgets keep their persisted
 * Designer IDs all the way through this export.
 */
export function buildHomeAssistantAutomationObjectsForLayer({
  layer,
  plateName = 'openhasp',
  validationErrors,
  // How to read an object's HA configuration (the Exporter includes the flow graph).
  configOf = object => object.hass_config,
  // State-trigger rules of a page's flow graph (hassCompiler pageRules).
  pageRulesOf = () => [],
}) {
  const rawLayer = toRaw(layer);
  const layerChildren = rawLayer?.getChildren?.() ?? [];
  const pages = layerChildren.filter(page => page?.obj === 'page');
  const templates = layerChildren.filter(page => page?.obj === 'template_page');
  const automations = [];

  const appendObjectConfigs = (root, pageId, idForObject) => {
    const visited = new Set();
    const visit = object => {
      object = toRaw(object);
      if (!object || visited.has(object)) return;
      visited.add(object);

      const hassConfig = configOf(object);
      if (hassConfig) {
        const runtimeObjectId = idForObject(object);
        if (runtimeObjectId != null) {
          automations.push(...buildHomeAssistantAutomationObjects({
            page_id: pageId,
            hasp_id: runtimeObjectId,
            object,
            hass_config: hassConfig,
            plateName,
            validationErrors,
          }));
        }
      }
      (object.getChildren?.() ?? []).forEach(visit);
    };
    (root.getChildren?.() ?? []).forEach(visit);
  };

  for (const page of pages) {
    const pageId = Number(page.haspid ?? page.attrs?.haspid);
    appendObjectConfigs(page, pageId, object => object.haspid ?? object.attrs?.haspid);
    automations.push(...buildHomeAssistantStateAutomations({
      page_id: pageId, rules: pageRulesOf(page), plateName, validationErrors,
    }));
  }

  for (const template of templates) {
    const pageId = Number(template.targetPage);
    if (!Number.isInteger(pageId)) continue;
    appendObjectConfigs(template, pageId, object => object.haspid ?? object.attrs?.haspid);
  }

  return automations;
}

/** Serialize automations collected from a complete plate/layer. */
export function generateHomeAssistantAutomationYamlForLayer(options) {
  const automations = buildHomeAssistantAutomationObjectsForLayer(options);
  return automations.length > 0
    ? stringifyHomeAssistantAutomations(automations)
    : '';
}

/** Serialize the already-built Home Assistant automation objects once. */
export function generateHomeAssistantAutomationYaml(options) {
  const automations = buildHomeAssistantAutomationObjects(options);
  return automations.length > 0
    ? stringifyHomeAssistantAutomations(automations)
    : '';
}
